"use client";

import Link from "next/link";
import Image from "next/image";
import { Button } from "../Button";
import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";

import { BlogsResponse, BlogPost } from "@/lib/actions/blog.actions";

// Support both old API and new CMS API response formats
interface LegacyApiResponse {
  results?: Array<{
    title?: string;
    description?: string;
    image?: string;
    slug?: string;
  }>;
}

export default function FeaturedBlogPost({ data }: { data: BlogsResponse | LegacyApiResponse }) {
  // Support both old API (results) and new CMS API (posts)
  const posts = (data as BlogsResponse)?.posts || (data as LegacyApiResponse)?.results || [];
  const firstPost = posts[0] as BlogPost | undefined;

  // Map fields for compatibility
  const title = firstPost?.title || "";
  const description = firstPost?.excerpt || (firstPost as unknown as { description?: string })?.description || "";
  const image = firstPost?.featured_image || (firstPost as unknown as { image?: string })?.image || "";
  const slug = firstPost?.slug || "";

  const imageRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const containerRef = useRef<HTMLElement>(null);

  // Check if we have a valid post to display
  const hasPost = Boolean(firstPost && (title || slug));

  useEffect(() => {
    // Don't run animations if there's no post
    if (!hasPost) return;

    const imageElement = imageRef.current;
    const titleElement = titleRef.current;
    const descriptionElement = descriptionRef.current;
    const buttonElement = buttonRef.current;
    const containerElement = containerRef.current;

    if (!containerElement) return;

    // Filter out null elements before animating
    const elementsToAnimate = [imageElement, titleElement, descriptionElement, buttonElement].filter(Boolean);
    if (elementsToAnimate.length === 0) return;

    gsap.set(elementsToAnimate, {
      opacity: 0,
    });

    if (imageElement) {
      gsap.set(imageElement, {
        y: 40,
        filter: "blur(8px)",
      });
    }

    const textElements = [descriptionElement, buttonElement, titleElement].filter(Boolean);
    if (textElements.length > 0) {
      gsap.set(textElements, {
        y: 30,
        filter: "blur(8px)",
      });
    }

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerElement,
          start: "top 80%",
          end: "top 40%",
          toggleActions: "play none none reverse",
        },
      });

      if (imageElement) {
        tl.to(imageElement, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          filter: "blur(0px)",
          ease: "power2.out",
        });
      }

      if (titleElement) {
        tl.to(
          titleElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "power2.out",
          },
          "-=1"
        );
      }

      if (descriptionElement) {
        tl.to(
          descriptionElement,
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            filter: "blur(0px)",
            ease: "power2.out",
          },
          "-=1"
        );
      }

      if (buttonElement) {
        tl.to(
          buttonElement,
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            filter: "blur(0px)",
            ease: "power2.out",
          },
          "-=0.5"
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, [hasPost]);

  // Don't render the section if there are no posts
  if (!hasPost) {
    return null;
  }

  return (
    <>
      <section ref={containerRef} className="container py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center bg-white p-6 md:p-8 rounded-lg shadow-lg border border-gray-100">
          {image && (
            <div
              ref={imageRef}
              className="w-full max-h-80 max-w-[500px] mx-auto rounded-lg overflow-hidden opacity-0"
            >
              <Image
                src={image}
                alt={title || "Featured blog post image"}
                width={800}
                height={600}
                quality={85}
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 80vw, 800px"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="flex flex-col justify-center">
            <div className="">
              {title && (
                <h2
                  ref={titleRef}
                  className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-theme-secondary capitalize mb-4 opacity-0"
                >
                  {title}
                </h2>
              )}

              {description && (
                <p
                  ref={descriptionRef}
                  className="mb-6 text-sm md:text-base text-[#4B5563] opacity-0"
                >
                  {description}
                </p>
              )}
            </div>

            {slug && (
              <Link ref={buttonRef} href={`/blogs/${slug}`} className="opacity-0">
                <Button className="w-full rounded-lg py-3 px-6 font-semibold leading-[21px] cursor-pointer transform hover:scale-105 hover:bg-orange-400 transition-all duration-300 relative overflow-hidden group before:content-[''] before:absolute before:top-0 before:left-[-100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-[100%]">
                  <span className="relative z-10 transition-all duration-300 text-white group-hover:text-white">
                    Read More
                    <span className="sr-only"> {title} </span>
                  </span>
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
